<template>
  <with-actions>
    <user-modal ref="create_user" @done="getUsers"></user-modal>
    <customer-note-modal ref="create_customer_note" @done="getCustomer" />
    <sweet-modal ref="installation_note_modal" title="Installation note">
      <div class="form-group" v-if="customer">
        <textarea class="form-control" v-model="customer.installation_note"></textarea>
      </div>
      <div class="form-group">
        <button class="btn btn-primary" @click="spin($event, updateInstallationNote)">
          {{ 'save' | t }}
        </button>
      </div>
    </sweet-modal>
    <customer-preview-invoice-modal ref="invoice_preview_modal" :previewed-invoice="previewed_invoice" />
    <customer-create-discount-modal
      ref="create_discount"
      :customer="customer"
      @discountCreated="(discount) => discounts.push(discount)"
    />
    <div class="form-inline" v-if="customer" slot="context-actions">
      <customer-show-customer-header-routing
        :customer="customer"
        :currentTab="currentTab"
        :third-party-managed="thirdPartyManaged"
        @tabChange="handleTabChange"
      />
    </div>
    <div class="form-inline" v-if="customer" slot="context-actions">
      <customer-show-customer-header-tab
        :customer="customer"
        :currentTab="currentTab"
        :users="users"
        :can-copy-local-impersonation-link="canCopyLocalImpersonationLink"
        :third-party-managed="thirdPartyManaged"
        @impersonateUser="impersonateUser"
        @impersonate="impersonate"
        @impersonateInLocalFrontend="impersonateInLocalFrontend"
        @createDiscount="$refs.create_discount.open()"
        @createUser="$refs.create_user.open()"
      />
    </div>
    <div class="alert alert-info" v-if="can('read_organizations') && thirdPartyManaged">
      This customer is managed by {{ customer.organization.name }} and therefore have limited options
    </div>
    <div class="alert alert-success" v-if="can('read_organizations') && thirdPartyManaged && isWebshipperSupported">
      This customer is supported by Webshipper
    </div>
    <div class="row">
      <div class="col-12">
        <div class="row">
          <div class="col-12"></div>
        </div>
        <div class="row">
          <div class="mb-4 col-sm-8">
            <customer-show-customer-tab
              :customer="customer"
              :current-tab="currentTab"
              :payments="payments"
              :invoices="invoices"
              :users="users"
              :discounts="discounts"
              :third-party-managed="thirdPartyManaged"
              :whitelabel-organizations="whitelabelOrganizations"
              :whitelabel-plans="whitelabelPlans"
              :staff-accesses="staffAccesses"
              @deleteDiscount="deleteDiscount"
              @deletePayment="deletePayment"
              @archiveCustomer="archiveCustomer"
              @confirmModal="confirmModal"
              @getStaffAccesses="getStaffAccesses"
              @onboard="onboard"
            />
          </div>
          <div class="col-sm-4" v-if="customer">
            <customer-show-customer-info-summary-card
              v-if="!thirdPartyManaged"
              :customer="customer"
              :hubspot-portal-id="hubspotPortalId"
              @preview-invoice="previewInvoice"
            />
            <customer-show-customer-info-notes-card
              v-if="customer && can('read_customer_notes')"
              :customer="customer"
              @createModal="
                $refs.create_customer_note.open({
                  note: '',
                  customer: customer
                })
              "
              @openModal="(note) => $refs.create_customer_note.open(Object.assign(note, { customer: customer }))"
            />
            <customer-show-customer-info-installation-note-card
              v-if="customer"
              :customer="customer"
              @openModal="$refs.installation_note_modal.open()"
            />
            <customer-show-customer-info-agents-card :customer="customer" v-if="!thirdPartyManaged" />
            <div class="mb-4 card">
              <div class="card-body" v-if="customer">
                <h4>Tags</h4>
                <hr />
                <h5>
                  <span class="mt-2 mr-2 badge badge-dark" v-for="(tagging, idx) in customer.taggings" :key="idx">
                    <span class="pr-2">{{ tagging.tag.name }}</span>
                    <i class="fa fa-times" role="button" @click="removeTagging(tagging.id, idx)"></i>
                  </span>
                </h5>
                <input
                  class="mt-3 form-control form-control-sm"
                  v-model="newTag"
                  placeholder="add new tag"
                  @keyup.enter="addTagging(newTag)"
                />
                <button
                  class="d-block btn btn-link"
                  @click="addTagging(tag.name)"
                  v-for="(tag, idx) in tagSuggestions"
                  :key="idx"
                >
                  {{ tag.name }}
                </button>
              </div>
            </div>
            <div class="mb-5 card" v-if="!thirdPartyManaged">
              <div class="card-body" v-if="customer">
                <h4>Agreement</h4>
                <hr />
                <em v-if="!customer.agreed">Customer has not agreed to terms yet</em>
                <pre class="text-muted" v-else>{{ customer.agreement }}</pre>
                <button
                  class="btn btn-danger"
                  v-if="customer.agreement"
                  @click="
                    confirmModal(
                      $event,
                      $i18n.t('are_you_sure'),
                      'Customer will be show agreement pop-up again',
                      removeAgreement
                    )
                  "
                >
                  Reset agreement
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </with-actions>
  <!---->
</template>

<script>
import store from '@/store';
import { mapState } from 'vuex';
import config from '@/config';
import Api from '@/store/jsonapi';
import clientApi from '@/store/clientapi';
import Alerts, { handle } from '@/utils/alerts.js';
import _ from 'lodash';
import showFile from '@/utils/show_file';
import pdf_utils from '@/utils/pdf_utils';
import customerCreateDiscountModal from './show/customer-create-discount-modal.vue';

export default {
  components: { customerCreateDiscountModal },
  props: ['customer_id'],
  data: function () {
    return {
      customer: null,
      previewed_invoice: null,
      users: [],
      hubspotPortalId: '6709594',
      currentTab: 'customer',
      plans: [],
      staffAccesses: [],
      payments: [],
      invoices: [],
      tags: [],
      discounts: [],
      newTag: '',
      targetOrganization: null,
      target_plan: null,
      whitelabelOrganizations: [],
      whitelabelPlans: []
    };
  },
  computed: {
    ...mapState(['currentTenant']),
    latestPayment() {
      return this.payments && this.payments[0];
    },
    thirdPartyManaged() {
      if (this.customer) {
        return this.customer.third_party_managed;
      }
      return false;
    },
    isWebshipperSupported() {
      if (this.customer && this.customer.organization) {
        return this.customer.organization.is_webshipper_supported;
      }
    },
    tagSuggestions() {
      return _.filter(
        this.tags,
        (t) => this.newTag.length > 0 && _.startsWith(_.lowerCase(t.name), _.lowerCase(this.newTag))
      );
    },
    /**
     * Returns true if not a white-label customer and also if using local backend
     */
    canCopyLocalImpersonationLink() {
      const fullUrl = document.URL;
      return fullUrl.includes('backend.local') || this.can('read_developer_actions');
    },
    customerStatusBadgeClass() {
      switch (this.customer.subscription?.status) {
        case 'active':
          return 'badge-success';
        case 'trial':
          return 'badge-warning';
        case 'unbilled':
          return 'badge-dark';
        case 'cancelled':
          return 'badge-danger';
        default:
          break;
      }
    },
    isOnSecureConnection() {
      return window.location.protocol === 'https:';
    },
    //Returns true if there is a DPA
    agreedDPA() {
      return !!this.customer?.data_processing_agreement_accepted_version;
    }
  },

  methods: {
    move_organization: handle(async function (targetOrganization, target_plan) {
      await Api.update('customers', {
        id: this.customer_id,
        billed_plan: target_plan,
        organization: targetOrganization
      });

      Alerts.success({
        title: this.$t('customer_moved') + targetOrganization.name
      });
      this.$router.push({ name: 'customers' });
    }),
    async previewInvoice() {
      let { data } = await Api.create('invoice_previews', {
        customer_id: this.customer.id
      });

      this.previewed_invoice = data.invoice;
      this.$refs.invoice_preview_modal.open();
    },
    async updateInstallationNote() {
      await Api.update('customers', {
        id: this.customer.id,
        installation_note: this.customer.installation_note
      });
      this.$refs.installation_note_modal.close();
    },
    async deleteDiscount(discount, idx) {
      await Api.destroy('discounts', discount.id);
      this.discounts.splice(idx, 1);
    },
    createDiscount: handle(async function () {
      let { data: discount } = await Api.create('discounts', {
        ...this.new_discount,
        customer: this.customer
      });

      this.new_discount = {
        value: 1,
        discount_type: 'fixed',
        comment: ''
      };

      this.$refs.create_discount.close();
      this.discounts.push(discount);
    }),
    async removeAgreement() {
      await Api.update('customers', { id: this.customer.id, agreement: '' });
      this.customer.agreement = null;
      this.customer.agreed = false;
    },
    async onboard() {
      let status = this.customer.onboarding_status;
      if (this.customer.onboarding_status == 'onboarding') {
        status = 'onboarded';
      } else {
        status = 'migrated';
      }
      Api.update('customers', {
        id: this.customer.id,
        onboarding_status: status
      })
        .then(async (data) => {
          Alerts.success({ title: 'Status updated', detail: '' });
          await Api.create('customer_notes', {
            customer: this.customer,
            note: 'Onboarding/migration was completed'
          });
          this.getCustomer();
        })
        .catch((err) => {
          _.map(err.errors, (error) => {
            Alerts.danger(error);
          });
        });
    },
    async addTagging(tag_name) {
      let tag = _.find(this.tags, (t) => _.lowerCase(t.name) === _.lowerCase(tag_name));
      if (!tag) {
        tag = (
          await Api.create('tags', {
            name: tag_name,
            taggable: { type: 'customers', id: this.customer.id }
          })
        ).data;
        this.tags.push(tag);
      }

      let { data: tagging } = await Api.create('taggings', {
        tag: { id: tag.id },
        taggable: { type: 'customers', id: this.customer.id }
      });
      tagging.tag = tag;

      this.customer.taggings.push(tagging);
      this.newTag = '';
    },
    async removeTagging(tagging_id, idx) {
      await Api.destroy('taggings', tagging_id);
      this.customer.taggings.splice(idx, 1);
    },
    async deletePayment(payment) {
      await Api.destroy('payments', payment.id);

      if (payment.invoice_id) {
        await Api.update('invoices', {
          id: payment.invoice_id,
          payment_status: 'unpaid'
        });
      }

      Alerts.success({ title: 'Payment deleted', detail: '' });
      this.loadPayments();
    },
    async loadPayments() {
      await Api.findAll('payments', {
        filter: { customer_id: this.customer_id },
        sort: '-id'
      }).then(({ data }) => {
        this.payments = data;
      });
    },
    download: async function (invoice) {
      await Api.find('invoices', invoice.id, {
        fields: { invoices: 'base64,invoice_no' }
      }).then(({ data }) => {
        this.showFile(
          this.b64toBlob(data.base64, 'application/pdf'),
          data.invoice_no + ' (' + this.customer.name + ')'
        );
      });
    },
    showFile,
    b64toBlob: pdf_utils.b64toBlob,
    frontendDomain() {
      // If there  is a domain_name attached to the organization (for whitelabel)
      // Use the domain and make sure it starts with a point so it can get concatenated with the URL

      // Only use the organization domain name if running in production
      if (this.customer.organization.domain_name && config.environment === 'production') {
        if (this.customer.organization.domain_name[0] !== '.') {
          return '.' + this.customer.organization.domain_name;
        }
        return this.customer.organization.domain_name;
      }
      // Use default domain
      return config.frontend.domain;
    },
    /**
     * Copies impersonation link to clipboard
     */
    impersonateInLocalFrontend() {
      const localDevelopmentEnvironmentLink =
        'http://' +
        this.customer.tenant_name +
        '.local.webshipr.com:9002' +
        '/oauth/impersonate?access_token=' +
        this.currentTenant.token +
        '&refresh_token=' +
        this.currentTenant.refreshToken;

      window.open(localDevelopmentEnvironmentLink, '_blank');
    },
    impersonate() {
      window.open(
        config.frontend.protocol +
          this.customer.tenant_name +
          this.frontendDomain() +
          '/oauth/impersonate?access_token=' +
          this.currentTenant.token +
          '&refresh_token=' +
          this.currentTenant.refreshToken,
        '_blank'
      );
    },
    async impersonateUser(user) {
      await Api.create('customer_tokens', {
        tenant: this.customer.tenant_name,
        user_id: user.id
      }).then(({ data }) => {
        window.open(
          config.frontend.protocol +
            this.customer.tenant_name +
            this.frontendDomain() +
            '/oauth/impersonate?access_token=' +
            data.token +
            '&refresh_token=' +
            data.refresh_token,
          '_blank'
        );
      });
    },
    async getCustomer() {
      let { data: customer } = await Api.find('customers', this.customer_id, {
        include:
          'organization,taggings.tag,customer_notes.staff,onboarding_agent,sales_agent,customer_success_agent,purchases,replaced_subscriptions'
      });
      customer.customer_notes = customer.customer_notes.slice().reverse();

      this.customer = customer;

      if (this.customer)
        this.$store.commit('setBreadcrumbs', [
          { to: { name: 'customers' }, text: 'Customers' },
          {
            to: { name: 'customers_show', customer_id: this.customer.id },
            text: this.customer.name
          }
        ]);

      if (!this.can('write_customer_tokens')) {
        return;
      }
      if (!this.currentTenant || this.customer.tenant_name != this.currentTenant.tenant) {
        let { data: token } = await Api.create('customer_tokens', {
          tenant: customer.tenant_name
        });
        this.$store.commit('setCurrentTenant', {
          tenantName: token.tenant,
          token: token.token,
          refreshToken: token.refresh_token,
          expiresAt: token.expires_at
        });
      }
      this.getUsers();
    },
    async getUsers() {
      await clientApi.findAllPages('users', { include: 'user_group,consents' }).then(({ data }) => {
        this.users = data;
      });
    },
    async getStaffAccesses() {
      await clientApi.findAll('staff_accesses', {}).then(({ data }) => {
        this.staffAccesses = data;
      });
    },
    tabClick(e) {
      this.currentTab = e.target.getAttribute('target');
    },
    async archiveCustomer() {
      const archiveDate = new Date().toJSON();
      await Api.create('archive_customer', {
        id: this.customer.id,
        archived_at: archiveDate
      }).then(({ data }) => {
        Alerts.success({ title: 'customer_archived_successfully' });
        this.customer.archived_at = archiveDate;
      });
    },
    handleTabChange(tab) {
      this.currentTab = tab;
    }
  },
  mounted: async function () {
    await this.getCustomer();
    Api.findAll('invoices', {
      filter: { customer_id: this.customer_id },
      sort: '-id'
    }).then(({ data }) => {
      this.invoices = data;
    });

    let { data: discounts } = await Api.findAll('discounts', {
      filter: { customer_id: this.customer_id }
    }); //   {filter: {customer_id: this.customer_id}, sort: '-id'}).then( ({data}) => {this.invoices = data})
    this.discounts = discounts;

    let { data: tags } = await Api.findAllPages('tags');
    this.tags = tags;

    this.loadPayments();

    Api.findAll('organizations', { fields: { organizations: 'id,name' } }).then(({ data }) => {
      this.whitelabelOrganizations = data;
    });
    if (this.can('read_plans')) {
      Api.findAllPages('plans', {
        include: 'organization',
        fields: { organizations: 'id,name' },
        filter: { currency: this.currency }
      }).then(({ data }) => {
        this.whitelabelPlans = data;
      });
    }
  },
  store
};
</script>

<style scoped>
.pre-wrap {
  white-space: pre-wrap;
}

.nowrap {
  white-space: nowrap;
}

.reset-modal-style {
  font-size: 14px !important;
  text-align: left !important;
}
</style>
